import { create } from 'zustand';
import request from '@/utils/request';

export interface CreatorProfile {
  id: number;
  email: string;
  displayName: string | null;
  avatarCosKey: string | null;
  avatarUrl: string | null;
  experimentBalanceCents: number;
  earningsBalanceCents: number;
  realNameVerified: boolean;
  status: 'active' | 'frozen' | 'deleted';
  createdAt: string;
  lastLoginAt: string | null;
}

interface AuthState {
  creator: CreatorProfile | null;
  ready: boolean;
  login: (
    email: string,
    password: string,
    captchaId: string,
    captchaAnswer: string,
  ) => Promise<void>;
  register: (
    email: string,
    password: string,
    captchaId: string,
    captchaAnswer: string,
    displayName?: string,
  ) => Promise<void>;
  logout: () => void;
  loadSession: () => Promise<void>;
  refreshBalances: () => Promise<void>;
  updateProfile: (patch: { displayName?: string; avatarCosKey?: string | null }) => Promise<void>;
  changePassword: (oldPassword: string, newPassword: string) => Promise<void>;
}

export const useAuthStore = create<AuthState>((set) => ({
  creator: (() => {
    const raw = localStorage.getItem('creator_user');
    return raw ? (JSON.parse(raw) as CreatorProfile) : null;
  })(),
  ready: false,
  async login(email, password, captchaId, captchaAnswer) {
    const res = await request.post<{ token: string; creator: CreatorProfile }>('/creator/login', {
      email,
      password,
      captchaId,
      captchaAnswer,
    });
    const data = res.data as unknown as { token: string; creator: CreatorProfile };
    localStorage.setItem('creator_token', data.token);
    localStorage.setItem('creator_user', JSON.stringify(data.creator));
    set({ creator: data.creator });
  },
  async register(email, password, captchaId, captchaAnswer, displayName) {
    const res = await request.post<{ token: string; creator: CreatorProfile }>(
      '/creator/register',
      { email, password, displayName, captchaId, captchaAnswer },
    );
    const data = res.data as unknown as { token: string; creator: CreatorProfile };
    localStorage.setItem('creator_token', data.token);
    localStorage.setItem('creator_user', JSON.stringify(data.creator));
    set({ creator: data.creator });
  },
  logout() {
    localStorage.removeItem('creator_token');
    localStorage.removeItem('creator_user');
    set({ creator: null });
    window.location.href = '/login';
  },
  async loadSession() {
    const token = localStorage.getItem('creator_token');
    if (!token) {
      set({ ready: true });
      return;
    }
    try {
      const res = await request.get<{ creator: CreatorProfile }>('/creator/session');
      const data = res.data as unknown as { creator: CreatorProfile };
      localStorage.setItem('creator_user', JSON.stringify(data.creator));
      set({ creator: data.creator, ready: true });
    } catch {
      localStorage.removeItem('creator_token');
      localStorage.removeItem('creator_user');
      set({ creator: null, ready: true });
    }
  },
  async refreshBalances() {
    const res = await request.get<{ experimentBalanceCents: number; earningsBalanceCents: number }>(
      '/creator/wallet',
    );
    const data = res.data as unknown as {
      experimentBalanceCents: number;
      earningsBalanceCents: number;
    };
    const raw = localStorage.getItem('creator_user');
    if (raw) {
      const prev = JSON.parse(raw) as CreatorProfile;
      const next: CreatorProfile = {
        ...prev,
        experimentBalanceCents: data.experimentBalanceCents,
        earningsBalanceCents: data.earningsBalanceCents,
      };
      localStorage.setItem('creator_user', JSON.stringify(next));
      set({ creator: next });
    }
  },
  async updateProfile(patch) {
    const res = await request.put<{ creator: CreatorProfile }>('/creator/profile', patch);
    const data = res.data as unknown as { creator: CreatorProfile };
    localStorage.setItem('creator_user', JSON.stringify(data.creator));
    set({ creator: data.creator });
  },
  async changePassword(oldPassword, newPassword) {
    await request.post('/creator/change-password', { oldPassword, newPassword });
  },
}));
